// The guided tours' steps. Each step may name its page (`route`; the tour goes there) and the
// element it explains (`target`: CSS selectors tried in order, the first visible one wins; none
// found → the text shows in the middle). The beginner tour walks through the whole site; the
// editor tour is the advanced one, for writing songs.
import { SLOW } from "../stream";
import type { TourId } from "./store";

export interface TourStep {
  route?: string;
  target?: string[];
  title: string;
  body: string;
  /** An extra button on this step (e.g. "Start the editor tour"). */
  action?: { label: string; tour: TourId };
}

const SONG_SECS = SLOW ? 53 : 30;
const BPM = SLOW ? 72 : 125;

export const SITE_TOUR: TourStep[] = [
  {
    route: "/",
    title: "Welcome to The Infinite",
    body: `One song that never ends, at ${BPM} BPM. It's written ${SONG_SECS} seconds at a time by people like you, and everyone listening hears the same bar at the same moment — like a radio station driven by a world clock. This tour shows you around in about a minute.`,
  },
  {
    route: "/",
    target: [".today"],
    title: "Today's sound",
    body: "Each day has its own key, drum kit and instruments: bass, harmony, lead, a classic instrument and an atmos kit (twelve pads of drones, air, choirs, risers, hits and sparkles). Every song of the day uses them, so the songs flow into each other. The bars underneath dance to the music.",
  },
  {
    route: "/",
    target: ['[data-testid="join"]'],
    title: "Join the stream",
    body: "The stream starts on its own when your browser allows sound — otherwise the first click anywhere starts it. This button leaves the stream (or joins again).",
  },
  {
    route: "/",
    target: [".river-lane", ".river-list"],
    title: "The River",
    body: "The songs flow past the glowing playhead. Click a card to see who wrote it, rate it or like it ♥; “+” puts it on one of your tapes. Faded cards have played; the ones on the right are coming up.",
  },
  {
    route: "/",
    target: [".view-switch"],
    title: "River or list",
    body: "Prefer a plain list? Switch the view here — same songs, same order.",
  },
  {
    route: "/",
    target: ['[data-testid="tempo-switch"]'],
    title: "Two tempos",
    body: SLOW
      ? "You're on The Infinite 72, the chill sister site. This switch takes you back to 125 BPM. Your account, likes and tapes are shared."
      : "The Infinite has a chill sister: The Infinite 72, with slow jams and double-time trap. This switch takes you there and back; your account, likes and tapes are shared.",
  },
  {
    route: "/",
    target: ['[data-testid="how-toggle"]'],
    title: "What am I listening to?",
    body: "At the bottom of the page: a short explanation of how the endless song works, whenever you want it.",
  },
  {
    route: "/calendar",
    target: [".calendar", "main h1"],
    title: "The calendar",
    body: "Every upcoming day with its key, kit and instruments. Pick one up to a week ahead to write a song for it; past days keep their songs to listen back.",
  },
  {
    route: "/write",
    target: [".editor-head"],
    title: "Write a song",
    body: `The editor: a ${SONG_SECS}-second song of 16 bars in the day's key and sounds. Notes snap to the key, so nothing sounds wrong. Your draft is saved as you go.`,
  },
  {
    route: "/write",
    target: ['[data-testid="track-grid"]', ".phone-editor"],
    title: "Click to make music",
    body: "Each row is a sound: drums on top, then bass, harmony, lead and classics as piano rolls. Click a cell to add a note, click it again to remove it. Press ▶ Play pattern to hear it.",
  },
  {
    route: "/write",
    target: ['[data-testid="wizard-jump"]'],
    title: "Stuck? Ask the Wizard",
    body: "The Wizard writes chords, bass lines, melodies, arpeggios and grooves with you. You hear its ideas first and keep only what you like; the Wizard's share is always shown on your song.",
  },
  {
    route: "/write",
    target: [".submit-panel"],
    title: "Send it in",
    body: "Signed in, you reserve a place in the day and submit. Automatic checks keep every song musical and explain anything to fix; approved songs play at their time in the stream.",
  },
  {
    route: "/fame",
    target: [".fame-grid", "main h1"],
    title: "Hall of Fame",
    body: "The best-rated writers and songs. Ratings and likes from listeners decide who's up here.",
  },
  {
    route: "/mixtapes",
    target: ["main h1"],
    title: "Top100 Mixtapes",
    body: "Every finished day becomes a mixtape of its 100 best songs, counted down. Popular tapes glow from yellow to red.",
  },
  {
    route: "/tapes",
    target: ["main h1"],
    title: "My Tapes",
    body: "Your own playlists: collect songs from any day with “+ Tape” and play them back to back, with each day's break in between.",
  },
  {
    target: ['.header-tools [role="radiogroup"]'],
    title: "Pick a look",
    body: "Studio Feel, Hifi Feel (light), the Zoo, where every sound is an animal that dances to its notes, or Eigenbloom, a tunnel of light drawn live by the notes.",
  },
  {
    target: ['[data-testid="join-link"]', '[data-testid="account-link"]'],
    title: "Join in",
    body: "An account lets you write songs, rate, like and make tapes. Signing in only needs your email address — no password.",
  },
  {
    target: ['[data-testid="tour-menu"]'],
    title: "That's it — have fun!",
    body: "You can take this tour again, or the advanced editor tour, any time from the ? Tours button. Ready to write? The editor tour shows every tool in detail.",
    action: { label: "Start the editor tour", tour: "editor" },
  },
];

export const EDITOR_TOUR: TourStep[] = [
  {
    route: "/write",
    target: [".editor-head"],
    title: "The editor, in depth",
    body: "Here's every tool, in about two minutes. Up top: the day you're writing for, its key, kit, instruments and tempo, how many notes your song has and whether it's saved.",
  },
  {
    route: "/write",
    target: ['[data-testid="play-loop"]'],
    title: "Play a pattern or the song",
    body: "▶ Play pattern loops the pattern you're editing; ▶ Play song plays all 16 bars as they'll sound in the stream. Space starts and stops.",
  },
  {
    route: "/write",
    target: ['[data-testid="record"]'],
    title: "Record live",
    body: "Play the pads or a MIDI keyboard while the pattern loops, and the notes land in the grid. Count-in gives you a bar of clicks first; Enter starts recording.",
  },
  {
    route: "/write",
    target: ['[data-testid="grid-select"]'],
    title: "Grid and quantize",
    body: "The grid sets the note size for clicking and the snap for recording (1/16 is a sixteenth note). Quantize recording snaps what you play; Quantize all tidies up the whole pattern.",
  },
  {
    route: "/write",
    target: ['[aria-label="Zoom"]'],
    title: "Zoom",
    body: "Show all 4 bars, or zoom in to 2 or 1 bar for fine work; a slider then scrolls through the pattern.",
  },
  {
    route: "/write",
    target: ['[aria-label="Pads play"]'],
    title: "What the pads play",
    body: "The 16 pads play drums — or switch them to an instrument and they play its scale, 16 notes in key (Oct − / + moves up and down). Extra lanes you add appear here too. Note repeat retriggers held pads in time; Enable MIDI connects a keyboard.",
  },
  {
    route: "/write",
    target: [".pads"],
    title: "The pads",
    body: "Click or tap a pad — higher on the pad is louder. On a computer keyboard: Z X C V, A S D F, Q W E R and 1 2 3 4, bottom row first, like a classic 4×4 pad sampler.",
  },
  {
    route: "/write",
    target: ['[data-testid="song-panel"]'],
    title: "Patterns and the song",
    body: "A song is up to three 4-bar patterns — say an intro (A), a groove (B) and an outro (C). The tabs choose the pattern you edit; + Copy duplicates it to vary. The Song row picks which pattern each of the four sections plays.",
  },
  {
    route: "/write",
    target: ['[data-testid="track-grid"]', ".phone-editor"],
    title: "The grid, like a pro",
    body: "Click to add or remove. Drag a note up or down for velocity, sideways for length (Alt always sets length, Shift fine-tunes). Grab a note's right edge to resize it. From the keyboard: arrows move, Enter toggles, + / − velocity, [ ] length.",
  },
  {
    route: "/write",
    target: ['[data-testid="lane-add-bass"]', '[data-testid^="lane-add-"]', ".phone-sounds"],
    title: "More lanes",
    body: "+ Lane adds another of the instrument's sounds as its own lane — two harmonies, a second lead, a bass layer. Each lane has its own notes and mix; × removes it. Click a lane's header to fold it away.",
  },
  {
    route: "/write",
    target: ['[data-testid="sound-picker"]'],
    title: "Sounds",
    body: "Each instrument offers the day's main sound and two alternates from the same family; clicking one plays a preview, and your song can pick a different one per instrument. Below: the day's atmos kit — twelve pads of atmosphere in the day's key, in the grid's Atmos lane and on the pads (set them to Atmos).",
  },
  {
    route: "/write",
    target: [".mix-panel"],
    title: "Mix and effects",
    body: "Pick a sound, then set its volume, pan, filter cutoff and reverb and delay sends. Every sound also has two insert effects: a drive (tape, tube, crunch, lo-fi) and a filter (high-pass, band-pass, wobble).",
  },
  {
    route: "/write",
    target: [".stage-panel"],
    title: "The stereo stage",
    body: "Your band seen from the audience: drag a sound left or right to pan it, up to put it further back (more room). Studio mix sets up a professional layout — kick, snare and bass centred and dry — and Mirror flips it.",
  },
  {
    route: "/write",
    target: ["#wizard"],
    title: "The Wizard",
    body: "Choose what it should write (chords, bass, melody, arp, harmony, fill or groove), for which sound and how busy. Its idea plays along as dashed ghost notes: accept it, try another, or discard it. Every idea you receive counts towards the Wizard mark on your song.",
  },
  {
    route: "/write",
    target: [".submit-panel"],
    title: "Checks and submitting",
    body: "Give your song a title and submit. The checks look at key, structure, loudness and clashes, and tell you in plain words what to fix. Approved songs are frozen and play at their place in the day.",
  },
  {
    route: "/write",
    target: ['[data-testid="undo"]'],
    title: "Undo everything",
    body: "Every change is one step: ⌘/Ctrl+Z undoes, Shift+⌘/Ctrl+Z redoes. Now go make something — the stream is waiting for your song!",
  },
];

export const TOURS: Record<TourId, TourStep[]> = { site: SITE_TOUR, editor: EDITOR_TOUR };
export const TOUR_NAMES: Record<TourId, string> = { site: "Beginner tour", editor: "Editor tour (advanced)" };
